import { type TFuncKey } from 'i18next';
import { useTranslation } from 'react-i18next';

type Props = {
  readonly forKey?: TFuncKey;
  readonly interpolation?: Record<string, unknown>;
  /** Keeps a number and its hyphenated word ("2-step") on one line in headings and intros. */
  readonly isNumberedWordKept?: boolean;
};

const numberedWord = /(\d+-\p{L}+)/u;

/**
 * A component to render a dynamic translation key.
 */
const DynamicT = ({ forKey, interpolation, isNumberedWordKept = false }: Props) => {
  const { t } = useTranslation();

  if (!forKey) {
    return null;
  }

  const translated = t(forKey, interpolation ?? {});

  if (typeof translated === 'string' && isNumberedWordKept && numberedWord.test(translated)) {
    // A non-breaking hyphen would come from a fallback font; a nowrap span keeps Geist's glyph.
    const [before = '', word = '', ...after] = translated.split(numberedWord);

    return (
      <>
        {before}
        <span className="whitespace-nowrap">{word}</span>
        {after.join('')}
      </>
    );
  }

  if (typeof translated === 'string') {
    // The fragment will ensure the component has the return type that is compatible with `JSX.Element`.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{translated}</>;
  }

  // The fragment will ensure the component has the return type that is compatible with `JSX.Element`.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{`Translation key ${forKey} is invalid.`}</>; // This would be great to have i18n as well. Not harmful for now.
};
export default DynamicT;
